// Courses live as templateID 3 rows on the old ERP backend (scheduling/pricing
// only — no slug, images, or descriptive content). Rich content stays sourced
// from src/mocks/siteData.ts, same as the pre-migration Supabase hook did
// (it merged a thin DB row over a mock fallback); we merge a thin ERP row
// over the same mock fallback instead, matched by course name.
import {
  addUpdateTemplateDataWeb, cell, deleteTemplateData, getMaxRecordidInTemplate, getTemplateDataList,
  isApiConnected, type TemplateRow,
} from "@/lib/apiClient";
import { normalizeOldAppDate, oldAppDateToTimestamp } from "@/lib/oldAppDate";

const COURSES_TEMPLATE_ID = 3;

// Category lookup list — a separate, real, registered templateID (confirmed
// live 2026-07-27): 8 rows, single field "Courselist" (write _1, read index
// 3). Distinct from templateID 3's own "Category Name" field — that one
// only has 6 distinct real values in practice (some of these 8, like
// "Sport Climbing" and "White Water Kayaking", are filed under a broader
// category — "Mountaineering"/"White Water Rafting" respectively — on the
// actual course/batch rows). This is the canonical category list for
// dropdowns/filters, not a stand-in for course records.
const CATEGORIES_TEMPLATE_ID = 2;
const CATEGORY_SCHEMA = { id: 0, name: 3 } as const;

export async function listCourseCategories(): Promise<string[]> {
  if (!isApiConnected()) return [];
  const { response } = await getTemplateDataList(CATEGORIES_TEMPLATE_ID, {}, { pgSize: 100 });
  return (response.records || [])
    .map((r) => cell(r, CATEGORY_SCHEMA.name).trim())
    .filter(Boolean);
}

// Same templateID 2 source as listCourseCategories(), but keeping the row
// `_id` alongside the name — used by the public /courses page (per explicit
// instruction) as its sole data source instead of templateID 3. TemplateID 2
// has exactly one real field (Courselist); every other course-card field
// (image, price, duration, description, etc.) has no backend source here
// and is filled with a safe placeholder by the caller.
export interface CourseCategoryEntry {
  id: string;
  name: string;
}

export async function listCourseCategoryEntries(): Promise<CourseCategoryEntry[]> {
  if (!isApiConnected()) return [];
  const { response } = await getTemplateDataList(CATEGORIES_TEMPLATE_ID, {}, { pgSize: 100 });
  return (response.records || [])
    .map((r) => ({ id: cell(r, CATEGORY_SCHEMA.id), name: cell(r, CATEGORY_SCHEMA.name).trim() }))
    .filter((r) => r.name);
}

// mocks/siteData.ts slug -> real Category Name, confirmed live 2026-07-24
// (same source data as batchService.ts's SLUG_TO_CATEGORY/SLUG_TO_COURSE_NAME
// maps, flattened to cover every mock course slug — including specific
// sub-courses, not just the 8 parent-category slugs). Used instead of
// matching by live course name (matchLiveCourseByName below): that fuzzy
// name match only succeeds for ~7 of 28 mock course slugs (real backend
// names differ too much from the mock names for most sub-courses — e.g.
// "Advance Mountaineering Course (AMC)" vs the mock's "Advanced
// Mountaineering Course (AMC)"), so deriving category from it left most
// courses stuck on their old land/aqua/aero grouping — invisible under any
// real-category filter except "All". Slug is a reliable identifier (unlike
// course name), so mapping by slug covers every course correctly.
const SLUG_TO_REAL_CATEGORY: Record<string, string> = {
  "basic-mountaineering-course": "Mountaineering",
  "advanced-mountaineering-course": "Mountaineering",
  "search-rescue-mountaineering": "Mountaineering",
  "method-of-instruction-mountaineering": "Mountaineering",
  mountaineering: "Mountaineering",
  "sports-climbing": "Mountaineering",
  "basic-mountain-terrain-biking": "Mountain Terrain Biking",
  "advanced-mountain-terrain-biking": "Mountain Terrain Biking",
  "mountain-terrain-biking": "Mountain Terrain Biking",
  "scuba-diving-basic": "SCUBA Diving",
  "scuba-diving-advanced": "SCUBA Diving",
  "search-rescue-scuba": "SCUBA Diving",
  "scuba-combined": "SCUBA Diving",
  "scuba-diving": "SCUBA Diving",
  "white-water-rafting-basic": "White Water Rafting",
  "white-water-rafting-intermediate": "White Water Rafting",
  "white-water-rafting": "White Water Rafting",
  "kayaking-basic": "White Water Rafting",
  kayaking: "White Water Rafting",
  "paragliding-basic-intermediate": "Paragliding",
  "paragliding-advanced": "Paragliding",
  "paragliding-combined": "Paragliding",
  "paragliding-refresher": "Paragliding",
  paragliding: "Paragliding",
  "paramotor-basic": "Paramotor",
  "paramotor-intermediate": "Paramotor",
  "paramotor-trike": "Paramotor",
  paramotor: "Paramotor",
};

export function realCategoryForSlug(slug: string): string | null {
  return SLUG_TO_REAL_CATEGORY[slug] ?? null;
}

// Read positions 0/3-16 are confirmed from the old app's usage AND directly
// verified against a live response (see docs/BACKEND_SPEC_MISSING_ENDPOINTS.md,
// "existing templates have a fixed, already-registered field count") —
// templateID 3 has EXACTLY 14 real registered fields (_1 sr through _14
// availableSeats). slug/status/difficulty/adminDuration/descriptions (17-22)
// are new admin-only fields this app wanted to add — confirmed live that they
// do NOT exist as registered fields and are silently dropped on every write
// until the backend registers them (same doc). They're kept in the schema
// and always written anyway (harmless no-ops today) so they start working
// automatically once registered, with no code change needed.
//
// IMPORTANT: every write MUST include a value for every field up to the
// highest index used (_1.._14 at minimum), even ones the UI doesn't collect —
// confirmed live that omitting a registered field from a write causes the
// backend to omit that array slot from the stored row entirely (not blank/
// null), which shifts every subsequent field's read position left by one per
// omitted field and silently corrupts the whole row on read.
// Write "_N" = read index - 2, matching the offset convention confirmed
// elsewhere (Students/Batches).
const SCHEMA = {
  id: 0,
  sr: 3,
  categoryName: 4,
  courseName: 5,
  duration: 6, // the REAL backend "Duration" field — distinct from the admin-only one below
  seats: 7,
  season: 8,
  serialNo: 9,
  datesFrom: 10,
  datesTo: 11,
  fees: 12,
  capacity: 13,
  age: 14,
  type: 15,
  availableSeats: 16,
  slug: 17,
  status: 18,
  difficulty: 19,
  adminDuration: 20,
  shortDescription: 21,
  fullDescription: 22,
} as const;

export interface LiveCourseInfo {
  id: string;
  categoryName: string;
  courseName: string;
  serialNo: string;
  seats: number;
  availableSeats: number;
  fees: number;
  datesFrom: string;
  datesTo: string;
  season: string;
  capacity: number;
  age: string;
  type: string;
}

function transformRow(row: TemplateRow[]): LiveCourseInfo {
  return {
    id: cell(row, SCHEMA.id),
    categoryName: cell(row, SCHEMA.categoryName),
    courseName: cell(row, SCHEMA.courseName),
    serialNo: cell(row, SCHEMA.serialNo),
    seats: Number(cell(row, SCHEMA.seats) || 0),
    availableSeats: Number(cell(row, SCHEMA.availableSeats) || 0),
    fees: Number(cell(row, SCHEMA.fees) || 0),
    datesFrom: cell(row, SCHEMA.datesFrom),
    datesTo: cell(row, SCHEMA.datesTo),
    season: cell(row, SCHEMA.season),
    capacity: Number(cell(row, SCHEMA.capacity) || 0),
    age: cell(row, SCHEMA.age),
    type: cell(row, SCHEMA.type),
  };
}

export async function listLiveCourses(): Promise<LiveCourseInfo[]> {
  if (!isApiConnected()) return [];
  const { response } = await getTemplateDataList(COURSES_TEMPLATE_ID, {}, { pgSize: 500 });
  return (response.records || []).map(transformRow);
}

export async function findLiveCourseBySerialNo(serialNo: string): Promise<LiveCourseInfo | null> {
  if (!isApiConnected()) return null;
  const { response } = await getTemplateDataList(COURSES_TEMPLATE_ID, { "Serial No": serialNo }, { pgSize: 5 });
  const row = response.records?.[0];
  return row ? transformRow(row) : null;
}

// Dates come back from the old app as "16-Mar-2027", not ISO —
// oldAppDateToTimestamp/normalizeOldAppDate (lib/oldAppDate.ts) handle this
// without going through Date()'s timezone-sensitive parsing, which was
// previously shifting every date a day earlier for any visitor in a
// UTC+ timezone (confirmed live 2026-08-20 in Asia/Calcutta).
const parseOldAppDate = oldAppDateToTimestamp;

// Same source dates, normalized to "YYYY-MM-DD" for <input type="date">
// (which silently renders blank on any non-ISO value) — confirmed live:
// without this, editing any real imported course showed empty Date From/To
// fields, which then failed the form's own "dates are mandatory" validation
// and silently blocked the edit from submitting at all.
const toIsoDate = normalizeOldAppDate;

// A course name can match MULTIPLE rows (each a separately scheduled batch
// — see docs/BACKEND_SPEC_MISSING_ENDPOINTS.md, "each row is one scheduled
// batch, not a course definition"). Picking the first match returned by the
// API is essentially arbitrary (whatever order the backend happens to
// return rows in); pick the soonest upcoming batch instead so the catalog's
// price/date overlay reflects a batch a visitor could actually book.
export function matchLiveCourseByName(liveCourses: LiveCourseInfo[], courseName: string): LiveCourseInfo | undefined {
  const normalized = courseName.toLowerCase();
  const matches = liveCourses.filter(
    (c) => c.courseName.toLowerCase() === normalized
      || c.courseName.toLowerCase().includes(normalized)
      || normalized.includes(c.courseName.toLowerCase()),
  );
  if (matches.length <= 1) return matches[0];

  const now = Date.now();
  const upcoming = matches.filter((c) => parseOldAppDate(c.datesFrom) >= now);
  const pool = upcoming.length > 0 ? upcoming : matches;
  return pool.reduce((soonest, c) => (parseOldAppDate(c.datesFrom) < parseOldAppDate(soonest.datesFrom) ? c : soonest));
}

// ─── Admin CRUD ───

export interface AdminCourseRow {
  id: string;
  sr: string;
  name: string;
  slug: string;
  category: string;
  status: string;
  price: number;
  difficulty: string;
  duration: string;
  seats: number;
  shortDescription: string;
  fullDescription: string;
  // The live backend rejects every Courses save (create AND update) unless
  // these four are present and valid — confirmed directly against production:
  // "Failed! Template Data Invalid Date Fields Course Dates From/To" and
  // "Failed! Template Data Mandatory Fields Age/Type does not exists" when
  // omitted. Included here (not just in CourseWrite) so the edit form can
  // prefill them from the row being edited.
  datesFrom: string;
  datesTo: string;
  age: string;
  type: string;
}

function transformAdminRow(row: TemplateRow[]): AdminCourseRow {
  return {
    id: cell(row, SCHEMA.id),
    sr: cell(row, SCHEMA.sr),
    name: cell(row, SCHEMA.courseName),
    slug: cell(row, SCHEMA.slug),
    category: cell(row, SCHEMA.categoryName),
    status: cell(row, SCHEMA.status) || "active",
    price: Number(cell(row, SCHEMA.fees) || 0),
    difficulty: cell(row, SCHEMA.difficulty),
    duration: cell(row, SCHEMA.adminDuration) || cell(row, SCHEMA.duration),
    seats: Number(cell(row, SCHEMA.seats) || 0),
    shortDescription: cell(row, SCHEMA.shortDescription),
    fullDescription: cell(row, SCHEMA.fullDescription),
    datesFrom: toIsoDate(cell(row, SCHEMA.datesFrom)),
    datesTo: toIsoDate(cell(row, SCHEMA.datesTo)),
    age: cell(row, SCHEMA.age),
    type: cell(row, SCHEMA.type),
  };
}

export interface AdminCourseListResult {
  courses: AdminCourseRow[];
  totalCount: number;
}

export async function listAllCoursesAdmin(page: number, pageSize: number): Promise<AdminCourseListResult> {
  if (!isApiConnected()) return { courses: [], totalCount: 0 };
  const { response } = await getTemplateDataList(COURSES_TEMPLATE_ID, {}, { index: page, pgSize: pageSize });
  const rows = (response.records || []).map(transformAdminRow);
  return { courses: rows, totalCount: response.totalRecords ?? response.count ?? rows.length };
}

export interface CourseWrite {
  name: string;
  slug: string;
  category: string;
  status: string;
  price: number;
  difficulty: string;
  duration: string;
  seats: number;
  shortDescription?: string;
  fullDescription?: string;
  // Required by the backend on every save — see AdminCourseRow's comment.
  datesFrom: string; // "YYYY-MM-DD"
  datesTo: string; // "YYYY-MM-DD"
  age: string; // e.g. "18-40"
  type: string; // e.g. "Mixed" / "Male" / "Female"
  // Preserves the row's existing Sr value across an edit — required on every
  // save to avoid the field-shift bug (see SCHEMA's comment above); omit only
  // when creating (createCourse generates a fresh one).
  sr?: string;
}

function toWriteFields(c: CourseWrite): Record<string, unknown> {
  return {
    _1: c.sr ?? "",
    _2: c.category,   // categoryName
    _3: c.name,        // courseName
    _4: c.duration,     // real backend Duration field — reuses the same text as _18 below
    _5: c.seats,        // seats
    _6: "All Season",   // Season — not collected by the admin form yet, defaulted
    _7: c.slug,          // Serial No — not collected by the admin form yet, reuses slug
    _8: c.datesFrom,
    _9: c.datesTo,
    _10: c.price,       // fees
    _11: c.seats,        // capacity (admin form has one "seats" field, used as both)
    _12: c.age,
    _13: c.type,
    _15: c.slug,
    _16: c.status,
    _17: c.difficulty,
    _18: c.duration,
    _19: c.shortDescription ?? "",
    _20: c.fullDescription ?? "",
  };
}

export async function createCourse(course: CourseWrite): Promise<void> {
  const { response: maxIdResp } = await getMaxRecordidInTemplate(COURSES_TEMPLATE_ID);
  const sr = String(maxIdResp?.maxid ?? 1);
  // _14 (availableSeats) is only set on create, initialized to full capacity —
  // never overwritten on update, so editing a course doesn't wipe out seats
  // already consumed by bookings once that pipeline is connected.
  await addUpdateTemplateDataWeb(COURSES_TEMPLATE_ID, { ...toWriteFields({ ...course, sr }), _14: course.seats });
}

export async function updateCourse(id: string, course: CourseWrite): Promise<void> {
  // NOTE: _14 (availableSeats) is deliberately not resent here (see
  // createCourse's comment on why it shouldn't be blindly overwritten), but
  // updates were confirmed live to NOT simply merge onto the existing row —
  // an update with a minimal payload still triggered the same mandatory-field
  // validation as a create. Whether _14 survives an update untouched or gets
  // dropped like an omitted field elsewhere hasn't been separately confirmed
  // — flagged as a follow-up once real seat-tracking depends on this value.
  await addUpdateTemplateDataWeb(COURSES_TEMPLATE_ID, toWriteFields(course), id);
}

export async function deleteCourses(ids: string[]): Promise<void> {
  await deleteTemplateData(COURSES_TEMPLATE_ID, ids);
}
